import * as THREE from 'three';
import type { BakedGlow } from './bake.ts';

const glowTexture = (() => {
  let tex: THREE.Texture | null = null;
  return (): THREE.Texture => {
    if (tex) return tex;
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const g = c.getContext('2d') as CanvasRenderingContext2D;
    const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    gr.addColorStop(0, 'rgba(255,255,255,1)');
    gr.addColorStop(0.25, 'rgba(255,255,255,.55)');
    gr.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = gr;
    g.fillRect(0, 0, 64, 64);
    tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  };
})();

const VERT = /* glsl */ `
attribute vec3 iPos;
attribute vec4 iCol;
attribute float iSize;
uniform float pull;
varying vec2 vUv;
varying vec4 vCol;
void main() {
  vUv = uv;
  vCol = iCol;
  vec4 mv = modelViewMatrix * vec4(iPos, 1.0);
  mv.xy += position.xy * iSize;
  // Pulled toward the camera so a glow resting on the ground isn't cut in half by it.
  mv.z += iSize * pull;
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = /* glsl */ `
uniform sampler2D map;
uniform float boost;
varying vec2 vUv;
varying vec4 vCol;
void main() {
  vec4 t = texture2D(map, vUv);
  gl_FragColor = vec4(vCol.rgb * boost, t.a * vCol.a);
  #include <colorspace_fragment>
}`;

/**
 * Camera-facing additive glows (eyes, flames, crystals, sparks) as one instanced draw call.
 * Positions are in world space; everything is re-filled each render tick.
 */
export class GlowLayer {
  readonly mesh: THREE.Mesh;
  private geo: THREE.InstancedBufferGeometry;
  private pos: Float32Array;
  private col: Float32Array;
  private size: Float32Array;
  private n = 0;
  private cap: number;
  private v = new THREE.Vector3();
  private sv = new THREE.Vector3();
  readonly material: THREE.ShaderMaterial;

  /** `pull`: how far (in glow sizes) each billboard is drawn toward the camera (0 = in place). */
  constructor(cap = 2048, blending: THREE.Blending = THREE.AdditiveBlending, pull = 0) {
    this.cap = cap;
    this.geo = new THREE.InstancedBufferGeometry();
    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo.index = quad.index;
    this.geo.setAttribute('position', quad.getAttribute('position'));
    this.geo.setAttribute('uv', quad.getAttribute('uv'));
    this.pos = new Float32Array(cap * 3);
    this.col = new Float32Array(cap * 4);
    this.size = new Float32Array(cap);
    this.geo.setAttribute('iPos', new THREE.InstancedBufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('iCol', new THREE.InstancedBufferAttribute(this.col, 4).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('iSize', new THREE.InstancedBufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    this.material = new THREE.ShaderMaterial({
      uniforms: { map: { value: glowTexture() }, boost: { value: 1 }, pull: { value: pull } },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending,
    });
    this.mesh = new THREE.Mesh(this.geo, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 10;
  }

  begin(): void {
    this.n = 0;
  }

  add(x: number, y: number, z: number, color: THREE.Color, size: number, opacity: number): void {
    if (this.n >= this.cap) return;
    const i = this.n++;
    this.pos[i * 3] = x;
    this.pos[i * 3 + 1] = y;
    this.pos[i * 3 + 2] = z;
    this.col[i * 4] = color.r;
    this.col[i * 4 + 1] = color.g;
    this.col[i * 4 + 2] = color.b;
    this.col[i * 4 + 3] = opacity;
    this.size[i] = size;
  }

  get count(): number {
    return this.n;
  }

  /** The glows added since `from`, to paste() again on a later fill. */
  copyFrom(from: number): Float32Array[] {
    return [this.pos.slice(from * 3, this.n * 3), this.col.slice(from * 4, this.n * 4), this.size.slice(from, this.n)];
  }

  /** Adds glows copied with copyFrom(), as if added one by one (what doesn't fit is left out). */
  paste([pos, col, size]: Float32Array[]): void {
    const k = Math.min((size as Float32Array).length, this.cap - this.n);
    if (k <= 0) return;
    this.pos.set((pos as Float32Array).subarray(0, k * 3), this.n * 3);
    this.col.set((col as Float32Array).subarray(0, k * 4), this.n * 4);
    this.size.set((size as Float32Array).subarray(0, k), this.n);
    this.n += k;
  }

  /** Adds a baked glow transformed by an instance matrix. */
  addLocal(m: THREE.Matrix4, g: BakedGlow, opacityMul: number): void {
    this.v.copy(g.pos).applyMatrix4(m);
    const s = this.sv.setFromMatrixScale(m).x;
    this.add(this.v.x, this.v.y, this.v.z, g.color, g.size * s, g.opacity * opacityMul);
  }

  end(): void {
    this.geo.instanceCount = this.n;
    (this.geo.getAttribute('iPos') as THREE.InstancedBufferAttribute).needsUpdate = true;
    (this.geo.getAttribute('iCol') as THREE.InstancedBufferAttribute).needsUpdate = true;
    (this.geo.getAttribute('iSize') as THREE.InstancedBufferAttribute).needsUpdate = true;
    this.mesh.visible = this.n > 0;
  }
}
